/**
 * Intuition Rating calibration.
 *
 * A tier computed off a single gameweek swings wildly week to week, so the
 * rating stays hidden for a user's first three SCORED gameweeks and shows
 * progress toward unlock instead. Everything here counts gameweeks the user
 * has actually been scored in — never gameweeks elapsed in the season — so a
 * player who joins at GW14 calibrates over GW14-16, not GW1-3.
 */

export const CALIBRATION_GAMEWEEKS = 3

export interface CalibrationState {
  /** Scored gameweeks so far, capped at CALIBRATION_GAMEWEEKS. */
  progress: number
  isCalibrating: boolean
  /** The user's own three calibration gameweek numbers, ascending. Slots the
   * user hasn't reached yet are projected forward from the last one they
   * have (auto-predicted gameweeks are scored too, so the window is
   * contiguous). */
  gameweeks: number[]
  /** Last gameweek of the window — the one the rating unlocks after. */
  unlockGameweek: number
}

/**
 * @param scoredGameweeks every gameweek number this user has been scored in,
 *   in any order (duplicates tolerated) — e.g. from their form history.
 * @param currentGameweek the gameweek they're in now, used only to place the
 *   window for a user with nothing scored yet.
 */
export function computeCalibrationState(
  scoredGameweeks: number[],
  currentGameweek?: number
): CalibrationState {
  const scored = [...new Set(scoredGameweeks)].sort((a, b) => a - b)
  const progress = Math.min(scored.length, CALIBRATION_GAMEWEEKS)

  const gameweeks: number[] = []
  for (let i = 0; i < CALIBRATION_GAMEWEEKS; i++) {
    if (i < scored.length) {
      gameweeks.push(scored[i])
      continue
    }
    const previous = gameweeks[i - 1]
    if (previous === undefined) {
      // Nothing scored yet: calibration starts with the gameweek they're in.
      gameweeks.push(currentGameweek ?? 1)
    } else {
      // The gameweek after the last one in the window — but never behind the
      // live gameweek, so a user whose first score is already a few weeks old
      // still gets a forward-looking window.
      gameweeks.push(Math.max(previous + 1, currentGameweek ?? 0))
    }
  }

  return {
    progress,
    isCalibrating: progress < CALIBRATION_GAMEWEEKS,
    gameweeks,
    unlockGameweek: gameweeks[CALIBRATION_GAMEWEEKS - 1],
  }
}
